import { Link, createFileRoute } from "@tanstack/react-router";
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  CircleDashed,
  Clock3,
  type LucideIcon,
  Pencil,
  Plus,
  SlidersHorizontal,
} from "lucide-react";
import type { ReactNode } from "react";
import {
  Badge,
  Button,
  buttonVariants,
  EmptyState,
  DropdownMenu,
  DropdownMenuChevron,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@rumbledirect/ui-v2";
import { cn } from "@rumbledirect/ui-v2/cn";
import { formatDateOrEmpty } from "@rumbledirect/ui-v2/format";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import {
  useGetAdminSelectorQueriesId,
  useGetAdminSelectorRevisions,
} from "@/lib/api/generated/selector/selector";
import { DeleteRevisionButton } from "@/steering/DeleteRevisionButton";
import { SelectorFilterGroups } from "@/steering/SelectorFilterGroups";
import { SelectorRanking } from "@/steering/SelectorRanking";
import { SelectorTest } from "@/steering/SelectorTest";
import { useSelectorNames } from "@/steering/useSelectorNames";
import {
  isEditable,
  type RevisionState,
  revisionLabel,
  revisionState,
} from "@/steering/selectorRevisions";
import type { SelectorSelectorQueryRevisionResourceSelectorQueryRevisionOutputJsonld as Revision } from "@/lib/api/generated/model";

interface SteeringDetailSearch {
  revision?: string;
}

export const Route = createFileRoute("/_authenticated/steering_/$queryId")({
  validateSearch: (search: Record<string, unknown>): SteeringDetailSearch => ({
    revision: typeof search.revision === "string" ? search.revision : undefined,
  }),
  component: SteeringDetailPage,
});

function SteeringDetailPage(): ReactNode {
  const { queryId } = Route.useParams();
  const { revision: requestedRevisionId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data, isPending, isError, refetch } =
    useGetAdminSelectorQueriesId(queryId);
  const revisionsQuery = useGetAdminSelectorRevisions({ query: queryId });
  // The API returns revisions in chronological order.
  const revisions = revisionsQuery.data?.member ?? [];
  const activeRevision = revisions.find(
    (revision) => revision.id === data?.activeRevision,
  );
  const selectedRevision =
    revisions.find((revision) => revision.id === requestedRevisionId) ??
    activeRevision ??
    revisions.at(-1);
  const badge = selectedRevision
    ? STATE_BADGES[revisionState(selectedRevision, activeRevision?.id)]
    : null;
  const names = useSelectorNames(selectedRevision?.configuration);
  const canCreate = hasPermission(useCurrentUser(), CREATE_PERMISSION);
  const selectRevision = (revision: Revision): void =>
    void navigate({
      search: {
        revision: revision.id === activeRevision?.id ? undefined : revision.id,
      },
    });

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/steering"
        search={{ page: 1 }}
        className="inline-flex w-fit items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Alle selectieregels
      </Link>
      {isPending ? (
        <div role="status">
          <span className="sr-only">Selectieregel wordt geladen…</span>
          <Skeleton className="h-9 w-72" />
        </div>
      ) : isError ? (
        <div role="alert" className="py-6">
          <p>De selectieregel kon niet worden geladen.</p>
          <Button
            className="mt-4"
            variant="outline"
            onClick={() => void refetch()}
          >
            Opnieuw proberen
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-3xl font-bold tracking-tight">
              {data.name}
            </h1>
            {badge ? (
              <Badge variant={badge.variant}>{badge.label}</Badge>
            ) : null}
            {selectedRevision ? (
              <RevisionAction
                queryId={queryId}
                revision={selectedRevision}
                state={revisionState(selectedRevision, activeRevision?.id)}
                onDeleted={() =>
                  void navigate({ search: { revision: undefined } })
                }
              />
            ) : null}
          </div>
          {revisionsQuery.isError ? (
            <div role="alert" className="py-6">
              <p>De versies konden niet worden geladen.</p>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => void revisionsQuery.refetch()}
              >
                Opnieuw proberen
              </Button>
            </div>
          ) : revisionsQuery.isPending ? (
            <div role="status">
              <span className="sr-only">Versies worden geladen…</span>
              <Skeleton className="h-16 w-full" />
            </div>
          ) : null}
          {selectedRevision ? (
            <>
              <RevisionBar
                revisions={revisions}
                selectedRevision={selectedRevision}
                activeRevision={activeRevision}
                onSelect={selectRevision}
              />
              <Tabs defaultValue="filter-groups">
                <TabsList>
                  <TabsTrigger value="filter-groups">Filtergroepen</TabsTrigger>
                  <TabsTrigger value="ranking">Rangschikking</TabsTrigger>
                  <TabsTrigger value="test">Testen</TabsTrigger>
                </TabsList>
                <TabsContent value="filter-groups" className="mt-5">
                  <SelectorFilterGroups
                    configuration={selectedRevision.configuration}
                    names={names}
                  />
                </TabsContent>
                <TabsContent value="ranking" className="mt-5">
                  <SelectorRanking
                    configuration={selectedRevision.configuration}
                    names={names}
                  />
                </TabsContent>
                <TabsContent value="test" className="mt-5" keepMounted>
                  <SelectorTest
                    key={selectedRevision.id}
                    configuration={selectedRevision.configuration}
                  />
                </TabsContent>
              </Tabs>
            </>
          ) : revisionsQuery.isSuccess ? (
            <EmptyState
              icon={SlidersHorizontal}
              title="Nog geen versies"
              description="Deze selectieregel heeft nog geen configuratie. Stel de eerste versie samen om herstellers te kunnen selecteren."
              action={
                canCreate ? (
                  <Link
                    to="/steering/$queryId/new"
                    params={{ queryId }}
                    className={buttonVariants()}
                  >
                    <Plus aria-hidden="true" />
                    Eerste versie aanmaken
                  </Link>
                ) : undefined
              }
            />
          ) : null}
        </>
      )}
    </div>
  );
}

const STATE_BADGES: Record<
  RevisionState,
  {
    readonly label: string;
    readonly variant: "success" | "neutral" | "progress";
  }
> = {
  active: { label: "Actief", variant: "success" },
  historical: { label: "Historische versie", variant: "neutral" },
  scheduled: { label: "Gepland", variant: "progress" },
  draft: { label: "Concept", variant: "neutral" },
};

const ITEM_STATE_LABELS: Record<
  Exclude<RevisionState, "historical">,
  string
> = { active: "Huidige versie", scheduled: "Gepland", draft: "Concept" };

const BAR_STYLES: Record<RevisionState, string> = {
  active: "border-border/70 bg-card",
  historical: "border-warning/35 bg-warning/5",
  scheduled: "border-progress/35 bg-progress/5",
  draft: "border-border bg-muted/50",
};

const STATUS_ICONS: Record<
  RevisionState,
  { readonly Icon: LucideIcon; readonly iconColor: string }
> = {
  active: { Icon: CheckCircle2, iconColor: "text-success" },
  historical: { Icon: CalendarClock, iconColor: "text-warning" },
  scheduled: { Icon: Clock3, iconColor: "text-progress" },
  draft: { Icon: CircleDashed, iconColor: "text-muted-foreground" },
};

const CREATE_PERMISSION = "admin_selector_query_create";
const UPDATE_PERMISSION = "admin_selector_query_update";
const DELETE_PERMISSION = "admin_selector_query_delete";
const LINK_BUTTON = cn(
  buttonVariants({ variant: "outline", size: "sm" }),
  "ml-auto",
);

/** Concepts and planned versions are edited in place; the rest are immutable and copied. */
function RevisionAction({
  queryId,
  revision,
  state,
  onDeleted,
}: {
  readonly queryId: string;
  readonly revision: Revision;
  readonly state: RevisionState;
  readonly onDeleted: () => void;
}): ReactNode {
  const user = useCurrentUser();
  if (isEditable(state))
    return (
      <div className="ml-auto flex items-center gap-2">
        {hasPermission(user, UPDATE_PERMISSION) ? (
          <Link
            to="/steering/$queryId/revisions/$revisionId/edit"
            params={{ queryId, revisionId: revision.id }}
            className={LINK_BUTTON}
          >
            <Pencil aria-hidden="true" />
            Bewerken
          </Link>
        ) : null}
        {hasPermission(user, DELETE_PERMISSION) ? (
          <DeleteRevisionButton
            queryId={queryId}
            revisionId={revision.id}
            isDraft={state === "draft"}
            onDeleted={onDeleted}
          />
        ) : null}
      </div>
    );
  return hasPermission(user, CREATE_PERMISSION) ? (
    <Link
      to="/steering/$queryId/new"
      params={{ queryId }}
      search={{ basedOn: revision.id }}
      className={LINK_BUTTON}
    >
      <Plus aria-hidden="true" />
      Nieuwe versie op basis hiervan
    </Link>
  ) : null;
}

interface RevisionBarProps {
  readonly revisions: readonly Revision[];
  readonly selectedRevision: Revision;
  readonly activeRevision: Revision | undefined;
  readonly onSelect: (revision: Revision) => void;
}

function RevisionBar({
  revisions,
  selectedRevision,
  activeRevision,
  onSelect,
}: RevisionBarProps): ReactNode {
  const state = revisionState(selectedRevision, activeRevision?.id);
  const startsAt = formatDateOrEmpty(selectedRevision.revisionAt);
  const next = revisions.find(
    (revision) =>
      revision.revisionAt !== null &&
      selectedRevision.revisionAt !== null &&
      Date.parse(revision.revisionAt) > Date.parse(selectedRevision.revisionAt),
  );
  const { minimumResults, limit } = selectedRevision.configuration;
  // Newest first, with drafts (no revisionAt) on top.
  const newestFirst = [...revisions].sort(
    (a, b) =>
      (b.revisionAt ? Date.parse(b.revisionAt) : Infinity) -
        (a.revisionAt ? Date.parse(a.revisionAt) : Infinity) ||
      Date.parse(b.createdAt) - Date.parse(a.createdAt),
  );
  const { Icon: StatusIcon, iconColor: statusIconColor } = STATUS_ICONS[state];
  const statusText = {
    active: `Actief sinds ${startsAt}`,
    historical: `Looptijd ${startsAt}${next ? ` - ${formatDateOrEmpty(next.revisionAt)}` : ""}`,
    scheduled: `Gepland voor ${startsAt}`,
    draft: revisionLabel(selectedRevision),
  }[state];

  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-2xl border px-5 py-4 text-sm sm:flex-row sm:items-center sm:justify-between",
        BAR_STYLES[state],
      )}
    >
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-6 gap-y-2">
        <span
          className={cn(
            "inline-flex items-center gap-2",
            state !== "active" && "font-medium",
          )}
        >
          <StatusIcon
            className={cn("size-4", statusIconColor)}
            aria-hidden="true"
          />
          {statusText}
        </span>
        <span className="text-muted-foreground">
          Minimaal {minimumResults} resultaat
          {limit === null ? "" : ` · maximaal ${limit}`}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {state !== "active" && activeRevision ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onSelect(activeRevision)}
          >
            Huidige versie
          </Button>
        ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button
              type="button"
              size="sm"
              variant="outline"
              aria-label={`Versie wijzigen, ${revisionLabel(selectedRevision)} geselecteerd`}
            >
              {revisionLabel(selectedRevision)}
              <DropdownMenuChevron />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-80">
            {newestFirst.map((revision) => {
              const itemState = revisionState(revision, activeRevision?.id);
              return (
                <DropdownMenuItem
                  key={revision.id}
                  onSelect={() => onSelect(revision)}
                  className="items-start py-2"
                >
                  <span className="mt-0.5 grid size-4 shrink-0 place-items-center">
                    {revision.id === selectedRevision.id ? (
                      <CheckCircle2
                        className="text-primary"
                        aria-hidden="true"
                      />
                    ) : null}
                  </span>
                  <span className="flex min-w-0 flex-1 items-center justify-between gap-3 font-medium">
                    <span>{revisionLabel(revision)}</span>
                    {itemState === "historical" ||
                    itemState === "draft" ? null : (
                      <span className="text-xs font-normal text-muted-foreground">
                        {ITEM_STATE_LABELS[itemState]}
                      </span>
                    )}
                  </span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
